export const LIST_WINDOW_SIZE = 30;
export const MIN_TEXT_QUERY_LENGTH = 2;
export const SMALL_LIST_THRESHOLD = 15;

export function isCompactOptionsList(optionsCount) {
  return optionsCount <= SMALL_LIST_THRESHOLD;
}

export function getMinQueryLength(optionsCount) {
  return isCompactOptionsList(optionsCount) ? 1 : MIN_TEXT_QUERY_LENGTH;
}

export function getSelectNoOptionsMessage(inputValue, optionsCount) {
  const trimmed = (inputValue ?? '').trim();
  const minLength = getMinQueryLength(optionsCount);

  if (!trimmed) {
    return isCompactOptionsList(optionsCount) ? 'Sem opções' : 'Digite para buscar';
  }

  if (trimmed.length < minLength && !/^\d+$/.test(trimmed)) {
    return minLength > 1 ? 'Digite ao menos 2 caracteres' : 'Sem opções';
  }

  return 'Sem opções';
}

export function normalizeText(str) {
  return String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function matchesTextByWordStart(text, query) {
  const q = normalizeText(query);
  if (!q) {
    return true;
  }

  const normalized = normalizeText(text);
  if (!normalized) {
    return false;
  }

  if (normalized.includes(q)) {
    return true;
  }

  return normalized.split(/\s+/).some((word) => word.startsWith(q));
}

export function scoreTextByWordStart(text, query) {
  const q = normalizeText(query);
  if (!q) {
    return 0;
  }

  const normalized = normalizeText(text);
  if (!normalized) {
    return 0;
  }

  if (normalized.split(/\s+/).some((word) => word.startsWith(q))) {
    return 3;
  }

  if (normalized.startsWith(q)) {
    return 2;
  }

  if (normalized.includes(q)) {
    return 1;
  }

  return 0;
}

export function matchesNumericField(value, query) {
  const q = normalizeText(query);
  if (!q || !/^\d+$/.test(q)) {
    return false;
  }

  if (value == null || value === '') {
    return false;
  }

  return normalizeText(String(value)).includes(q);
}

export function filterOptionsWithWindow(
  options,
  inputValue,
  {
    windowSize = LIST_WINDOW_SIZE,
    loadedCount,
    matchFn,
    sortFn,
    getSortLabel,
  } = {}
) {
  const q = normalizeText(inputValue?.trim());
  const compact = isCompactOptionsList(options.length);
  const minQueryLength = getMinQueryLength(options.length);

  if (!q) {
    if (compact) {
      return options;
    }
    const limit = loadedCount ?? windowSize;
    return options.slice(0, limit);
  }

  if (q.length < minQueryLength && !/^\d+$/.test(q)) {
    return [];
  }

  return options
    .filter((option) => matchFn(option, q))
    .sort((a, b) => {
      if (sortFn) {
        return sortFn(a, b, q);
      }

      const scoreDiff = scoreTextByWordStart(getSortLabel?.(a) ?? '', q)
        - scoreTextByWordStart(getSortLabel?.(b) ?? '', q);
      if (scoreDiff !== 0) {
        return scoreDiff;
      }

      return normalizeText(getSortLabel?.(a) ?? '').localeCompare(
        normalizeText(getSortLabel?.(b) ?? ''),
        'pt-BR'
      );
    });
}

export function matchSearchSelectOption({
  label,
  sublabel,
  input,
  sublabelNumericOnly = true,
  optionsCount,
}) {
  const trimmed = (input ?? '').trim();
  if (!trimmed) {
    return true;
  }

  const minQueryLength = getMinQueryLength(optionsCount ?? Number.MAX_SAFE_INTEGER);
  if (trimmed.length < minQueryLength && !/^\d+$/.test(trimmed)) {
    return false;
  }

  if (matchesTextByWordStart(label, trimmed)) {
    return true;
  }

  if (sublabel == null || sublabel === '') {
    return false;
  }

  if (sublabelNumericOnly) {
    return matchesNumericField(sublabel, trimmed);
  }

  return matchesTextByWordStart(String(sublabel), trimmed);
}
